import {
  Button,
  DeepPartial,
  GridItem,
  HStack,
  Link,
  StackDivider,
  VStack,
} from "@chakra-ui/react";
import { Formik } from "formik";
import {
  CheckboxContainer,
  CheckboxControl,
  CheckboxSingleControl,
  TextareaControl,
} from "formik-chakra-ui";
import React from "react";
import RadioInput from "../../form/fields/radio";
import Section from "../../form/section";
import {
  ApplicationTypeOptions,
  FunctionTypeOptions,
  PreferredMailingAddressOptions,
  TitleOptions,
} from "../options";
import * as Yup from "yup";
import {
  ApplicationType,
  PreferredMailingAddress,
  SignupFormData,
} from "../types";
import Card from "../../layout/card";
import DateInput from "../../form/fields/date";
import { extendYup } from "../../../utils/yupExtensions";
import ErrorSummary from "../../form/errorSummary";
import TextInput from "../../form/fields/text";

const cols = 4;

extendYup();

const validationSchema: Yup.SchemaOf<DeepPartial<SignupFormData>> =
  Yup.object().shape({
    applicationType: Yup.string().required("Membership type is required"),
    business: Yup.object().shape({
      tradingName: Yup.string().required("Business trading name is required"),
      address: Yup.string().required("Business address is required"),
    }),
    person: Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("A valid email is required")
        .required("A valid email is required"),
      mobile: Yup.string()
        .required("A valid mobile is required")
        .mobile("A valid mobile is required"),
      dob: Yup.string()
        .required("A valid date of birth is required")
        .dateString("A valid date of birth is required"),
    }),
    preferredMailing: Yup.object().shape({
      type: Yup.string().required("Preferred mailing address is required"),
      value: Yup.mixed().when("type", {
        is: PreferredMailingAddress.other,
        then: Yup.string().required("Alternative mailing address is required"),
      }),
    }),
    termsAndConditions: Yup.object().shape({
      reiv: Yup.boolean().oneOf([true], "You must accept REIV's T&C's"),
      hutly: Yup.boolean().oneOf([true], "You must accept Hutly's T&C's"),
    }),
    licenseNumber: Yup.string(),
    functions: Yup.array(),
  });

const blankInitialValues: DeepPartial<SignupFormData> = {
  applicationType: "",
  business: {
    address: "",
    tradingName: "",
  },
  functions: [],
  person: {
    dob: "",
    email: "",
    firstName: "",
    lastName: "",
    middleName: "",
    mobile: "",
    preferredName: "",
    title: undefined,
  },
  termsAndConditions: {
    reiv: false,
    hutly: false,
  },
  preferredMailing: {
    type: "",
    value: "",
  },
};

const MemberDetailsForm: React.FC<{
  initialValues: DeepPartial<SignupFormData>;
  handleSubmit: (values: SignupFormData) => void;
}> = ({ initialValues, handleSubmit }) => {
  return (
    <Card>
      <Formik
        initialValues={initialValues || blankInitialValues}
        onSubmit={(values) => {
          handleSubmit(values as SignupFormData);
        }}
        validationSchema={validationSchema}
      >
        {({
          values,
          handleSubmit,
          isSubmitting,
          isValid,
          errors,
          submitCount,
        }) => {
          return (
            <form noValidate onSubmit={handleSubmit}>
              <VStack
                divider={<StackDivider borderColor="gray.200" />}
                spacing={8}
                align="stretch"
              >
                <Section columns={cols} title="Application type">
                  <GridItem rowSpan={1} colSpan={[4, 2]}>
                    <RadioInput
                      options={ApplicationTypeOptions}
                      name="applicationType"
                      label="What type of membership?"
                      isRequired
                    />
                  </GridItem>
                  {values.applicationType ===
                    ApplicationType.licensedMember && (
                    <GridItem rowSpan={1} colSpan={[cols, 2]}>
                      <TextInput
                        name="licenseNumber"
                        label="Estate agent licence number"
                      />
                    </GridItem>
                  )}
                </Section>

                <Section title="Personal details">
                  <GridItem rowSpan={1} colSpan={cols}>
                    <RadioInput
                      options={TitleOptions}
                      name="person.title"
                      label="Title"
                      columns={cols}
                      direction={"row"}
                    />
                  </GridItem>
                  <GridItem rowSpan={1} colSpan={[4, 2]}>
                    <TextInput
                      name="person.firstName"
                      label="First Name"
                      isRequired
                    />
                  </GridItem>
                  <GridItem rowSpan={1} colSpan={[4, 2]}>
                    <TextInput
                      name="person.preferredName"
                      label="Preferred Name"
                    />
                  </GridItem>
                  <GridItem rowSpan={1} colSpan={cols}>
                    <TextInput name="person.middleName" label="Middle Name" />
                  </GridItem>

                  <GridItem rowSpan={1} colSpan={cols}>
                    <TextInput
                      name="person.lastName"
                      label="Last Name"
                      isRequired
                    />
                  </GridItem>

                  <GridItem rowSpan={1} colSpan={[cols, 2]}>
                    <DateInput
                      name="person.dob"
                      label="Date of birth"
                      isRequired
                    />
                  </GridItem>

                  <GridItem rowSpan={1} colSpan={[cols, 2]}>
                    <TextInput
                      name="person.mobile"
                      label="Mobile"
                      type="tel"
                      isRequired
                    />
                  </GridItem>
                  <GridItem rowSpan={1} colSpan={cols}>
                    <TextInput
                      name="person.email"
                      label="Email"
                      type="email"
                      isRequired
                    />
                  </GridItem>
                </Section>

                <Section title="Office details">
                  <GridItem rowSpan={1} colSpan={cols}>
                    <TextInput
                      name="business.tradingName"
                      label="Registered trading name"
                      isRequired
                    />
                  </GridItem>
                  <GridItem rowSpan={1} colSpan={cols}>
                    <TextareaControl
                      name="business.address"
                      label="Business address"
                      isRequired
                    />
                  </GridItem>
                  <GridItem rowSpan={1} colSpan={cols}>
                    <RadioInput
                      options={PreferredMailingAddressOptions}
                      name="preferredMailing.type"
                      label="Preferred mailing address"
                      direction="row"
                      isRequired
                    />
                  </GridItem>
                  {values.preferredMailing?.type ===
                    PreferredMailingAddress.other && (
                    <GridItem rowSpan={1} colSpan={cols}>
                      <TextareaControl
                        name="preferredMailing.value"
                        label="Enter Alternative mailing address:"
                        isRequired
                      />
                    </GridItem>
                  )}
                </Section>

                <Section title="Employment details">
                  <GridItem rowSpan={1} colSpan={cols}>
                    <TextInput name="position" label="Position/title" />
                  </GridItem>
                  <GridItem rowSpan={1} colSpan={cols}>
                    <CheckboxContainer
                      name="functions"
                      label="Real estate services that you personally provide, please tick all that apply:"
                    >
                      {FunctionTypeOptions.map((type) => (
                        <CheckboxControl
                          key={type.value}
                          name="functions"
                          value={type.value}
                        >
                          {type.text}
                        </CheckboxControl>
                      ))}
                    </CheckboxContainer>
                  </GridItem>
                </Section>
                <Section title="Terms And Conditions">
                  <GridItem rowSpan={1} colSpan={cols}>
                    <CheckboxContainer name="t">
                      <CheckboxSingleControl name="termsAndConditions.reiv">
                        I agree to the REIV{" "}
                        <Link
                          href="https://reiv.com.au/terms-conditions"
                          isExternal
                        >
                          Terms and Conditions
                        </Link>
                        {", "}
                        <Link
                          href="https://reiv.com.au/privacy-policy"
                          isExternal
                        >
                          REIV Privacy Policy
                        </Link>
                        {", "}
                        <Link
                          href="https://reiv.com.au/news-resources/library/reiv-constitution-as-amended-30-november-2015.aspx"
                          isExternal
                        >
                          REIV Constitution
                        </Link>
                        {", "}
                        <Link
                          href="https://reiv.com.au/news-resources/library/reiv-by-laws-as-amended-15-february-2016.aspx"
                          isExternal
                        >
                          REIV By Laws
                        </Link>
                        {", "}
                        <Link
                          href="https://reiv.com.au/NEWS/Library/Legal/REIV-Rules-of-Practice"
                          isExternal
                        >
                          REIV Rules of Practice
                        </Link>
                        {", "}
                        <Link
                          href="https://reiv.com.au/news/library/legal/reia-principles-of-conduct-2012.aspx"
                          isExternal
                        >
                          REIA National Principles of Conduct
                        </Link>{" "}
                        &{" "}
                        <Link
                          href="https://reiv.com.au/library/legal/reiv-membership-declaration.aspx"
                          isExternal
                        >
                          Application Declaration
                        </Link>
                      </CheckboxSingleControl>
                      <CheckboxSingleControl name="termsAndConditions.hutly">
                        I agree to the Hutly (the REIV contract technology
                        partner){" "}
                        <Link
                          href="https://hutly.com/terms-conditions"
                          isExternal
                        >
                          Terms and Conditions
                        </Link>{" "}
                        &{" "}
                        <Link
                          href="https://hutly.com/privacy-policy"
                          isExternal
                        >
                          Privacy Policy
                        </Link>
                      </CheckboxSingleControl>
                    </CheckboxContainer>
                  </GridItem>
                </Section>

                {!!errors && submitCount !== 0 && !isValid && (
                  <ErrorSummary errors={errors} />
                )}

                <HStack justify="start">
                  <Button
                    type="submit"
                    colorScheme={"brand"}
                    isLoading={isSubmitting}
                  >
                    Next
                  </Button>
                </HStack>
              </VStack>
            </form>
          );
        }}
      </Formik>
    </Card>
  );
};

export default MemberDetailsForm;
